// LinkPreview.jsx - 增强版，支持电商网站预览
import React, { useState, useEffect } from 'react';
import './LinkPreview.css';

const LinkPreview = ({ url }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!url) return;
    
    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 替换为你的 Cloudflare Worker URL
        const workerUrl = 'https://web-preview.2365333260p.workers.dev';
        const response = await fetch(`${workerUrl}?url=${encodeURIComponent(url)}`);
        
        if (!response.ok) {
          throw new Error('获取预览失败');
        }
        
        const data = await response.json();
        setPreview(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPreview();
  }, [url]);
  
  if (loading) {
    return <div className="link-preview-loading">加载预览中...</div>;
  }
  
  if (error) {
    return <div className="link-preview-error">错误: {error}</div>;
  }
  
  if (!preview) {
    return null;
  }
  
  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className={`link-preview-container ${preview.siteType ? `site-type-${preview.siteType}` : ''}`}
    >
      {preview.image && (
        <div className="link-preview-image">
          <img src={preview.image} alt={preview.title} />
        </div>
      )}
      <div className="link-preview-content">
        <div className="link-preview-header">
          {preview.favicon && (
            <img 
              src={preview.favicon} 
              alt="" 
              className="link-preview-favicon" 
            />
          )}
          <span className="link-preview-url">{new URL(url).hostname}</span>
        </div>
        
        <h3 className="link-preview-title">{preview.title}</h3>
        
        {preview.description && (
          <p className="link-preview-description">{preview.description}</p>
        )}
        
        {/* 电商网站特定信息 */}
        {preview.siteType === 'ecommerce' && (
          <div className="link-preview-ecommerce">
            {preview.brand && (
              <div className="link-preview-brand">
                <span className="link-preview-label">品牌:</span> {preview.brand}
              </div>
            )}
            
            {preview.price && (
              <div className="link-preview-price">
                <span className="link-preview-label">价格:</span>
                <span className="link-preview-price-value">
                  {preview.currency && getSymbolFromCurrency(preview.currency)}
                  {preview.price}
                </span>
              </div>
            )}
            
            {preview.availability && (
              <div className={`link-preview-availability ${preview.availability.replace(/\s+/g, '-').toLowerCase()}`}>
                <span className="link-preview-label">库存:</span>
                <span className="link-preview-availability-value">
                  {translateAvailability(preview.availability)}
                </span>
              </div>
            )}
            
            {preview.rating && (
              <div className="link-preview-rating">
                <span className="link-preview-label">评分:</span>
                <div className="stars-container">
                  {renderStars(preview.rating)}
                </div>
                <span className="rating-value">{preview.rating}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </a>
  );
};

// 辅助函数：从货币代码获取符号
function getSymbolFromCurrency(currency) {
  const symbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'KRW': '₩',
    'INR': '₹'
  };
  
  return symbols[currency] || '';
}

// 辅助函数：翻译可用性状态
function translateAvailability(availability) {
  const lowercase = availability.toLowerCase();
  
  if (lowercase.includes('in stock') || lowercase.includes('available')) {
    return '有货';
  } else if (lowercase.includes('out of stock') || lowercase.includes('sold out')) {
    return '无货';
  } else {
    return availability;
  }
}

// 辅助函数：渲染星级评分
function renderStars(rating) {
  const numRating = parseFloat(rating);
  if (isNaN(numRating)) return null;
  
  const fullStars = Math.floor(numRating);
  const hasHalfStar = numRating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  
  const stars = [];
  
  // 全星
  for (let i = 0; i < fullStars; i++) {
    stars.push(<span key={`full-${i}`} className="star full-star">★</span>);
  }
  
  // 半星
  if (hasHalfStar) {
    stars.push(<span key="half" className="star half-star">★</span>);
  }
  
  // 空星
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<span key={`empty-${i}`} className="star empty-star">☆</span>);
  }
  
  return stars;
}

export default LinkPreview;